import { Controller } from '@hotwired/stimulus'

export default class ProductCtaController extends Controller {
  static targets = ['addToCartButton', 'notifyMeButton']

  declare readonly addToCartButtonTarget: HTMLElement
  declare readonly notifyMeButtonTarget: HTMLElement
  declare readonly hasAddToCartButtonTarget: boolean
  declare readonly hasNotifyMeButtonTarget: boolean

  connect(): void {
    document.addEventListener('change', this.onVariantChange)
  }

  disconnect(): void {
    document.removeEventListener('change', this.onVariantChange)
  }

  onVariantChange = (event: Event): void => {
    const target = event.target
    if (!(target instanceof HTMLSelectElement)) return
    if (!/options\[/i.test(target.name)) return

    const selectedOption = target.selectedOptions[0]
    if (!selectedOption) return

    const isSoldOut = selectedOption.dataset.soldOut === 'true'
    this.toggleCta(isSoldOut)
  }

  addToCart(event: Event): void {
    // 1. Find the native select for Size (which might be hidden in a custom UI)
    const selectors = document.querySelectorAll<HTMLSelectElement>(
      '[name*="options[Size]"], [name*="options[size]"], select[aria-label="Size"]',
    )

    let sizeSelect: HTMLSelectElement | null = null
    let customTrigger: HTMLElement | null = null

    // 2. Identify the active/visible select component
    for (const select of Array.from(selectors)) {
      const wrapper = select.closest('[data-controller="select"]')

      if (wrapper) {
        // It's wrapped in our custom SelectController
        const trigger = wrapper.querySelector<HTMLElement>(
          '[data-select-target="trigger"]',
        )

        // Check visibility of the TRIGGER, not the select (since select is hidden)
        if (trigger && trigger.offsetParent !== null) {
          sizeSelect = select
          customTrigger = trigger
          break
        }
      } else if (select.offsetParent !== null) {
        // Fallback: standard native visible select
        sizeSelect = select
        break
      }
    }

    // 3. If a visible select exists and has no value (placeholder), intervene
    if (sizeSelect && !sizeSelect.value) {
      event.preventDefault()

      // Open UI
      setTimeout(() => {
        if (customTrigger) {
          customTrigger.click() // Toggle custom dropdown
        } else {
          sizeSelect?.focus()
          // Try to open native picker
          if (
            'showPicker' in sizeSelect! &&
            typeof sizeSelect!.showPicker === 'function'
          ) {
            try {
              sizeSelect!.showPicker()
            } catch (e) {
              console.warn('showPicker failed', e)
            }
          }
        }
      }, 200)
    }
  }

  notifyMe(): void {
    this.dispatch('notify-me', { bubbles: true })
  }

  private toggleCta(soldOut: boolean): void {
    if (!this.hasAddToCartButtonTarget || !this.hasNotifyMeButtonTarget) return

    if (soldOut) {
      this.addToCartButtonTarget.classList.add('hidden')
      this.notifyMeButtonTarget.classList.remove('hidden')
    } else {
      this.addToCartButtonTarget.classList.remove('hidden')
      this.notifyMeButtonTarget.classList.add('hidden')
    }
  }
}
